import React, { useState, useEffect } from "react";
import {
  MapPin,
  Pencil,
  Trash2,
  Plus,
  Home,
  Building,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AddAddressModal from "./AddAddressModal";
import EditAddressModal from "./EditAddressModal";
import axios from "axios";
import Swal from "sweetalert2";
import AddressSkeleton from "./AddressSkeleton";

const Address = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const storedUserData = localStorage.getItem("BricksNbar-Users-Data");
    if (storedUserData) {
      const parsedUserData = JSON.parse(storedUserData);
      setUserData(parsedUserData);
    }
  }, []);

  useEffect(() => {
    if (userData?._id) {
      fetchAddresses();
    }
  }, [userData]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/address/get-user-addresses/${userData._id}`
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      setAddresses(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setError("Failed to load addresses. Please try again.");
      setLoading(false);
    }
  };

  const handleEdit = (address) => {
    setSelectedAddress(address);
    setEditModalOpen(true);
  };

  const handleDelete = (addressId) => {
    Swal.fire({
      title: "Are you sure to delete this address?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(
            `${process.env.REACT_APP_API_URL}/address/delete-address/${addressId}`
          );

          setAddresses((prevAddresses) =>
            prevAddresses.filter((address) => address._id !== addressId)
          );

          Swal.fire({
            title: "Deleted!",
            text: "The address has been removed.",
            icon: "success",
          });
        } catch (error) {
          console.error("Error removing address:", error);
          Swal.fire({
            title: "Error!",
            text: "Something went wrong. Please try again.",
            icon: "error",
          });
        }
      }
    });
  };

  const handleAddNew = () => {
    setIsModalOpen(true);
  };

  const getAddressIcon = (type) => {
    return type.toLowerCase() === "home" ? (
      <Home className="w-5 h-5 text-white" />
    ) : (
      <Building className="w-5 h-5 text-white" />
    );
  };

  if (loading) {
    return <AddressSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-red-100 p-4 rounded-md text-red-700 text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between sm:items-center mb-6 flex-col sm:flex-row gap-3 items-start">
        <h2 className="text-2xl font-bold text-gray-800 ">My Addresses</h2>
        <button
          onClick={handleAddNew}
          className="flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-4 rounded-md transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add New Address
        </button>
      </div>

      {addresses.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg">
          <MapPin className="w-12 h-12 mx-auto text-gray-400" />
          <p className="mt-4 text-gray-600">
            You don't have any saved addresses yet.
          </p>
          <button
            onClick={handleAddNew}
            className="mt-4 bg-orange-600 hover:bg-orange-700 text-white py-2 px-6 rounded-md transition-colors"
          >
            Add Your First Address
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {addresses.map((address) => (
            <div
              key={address._id}
              className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-4 border-b bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <div className="bg-orange-500 p-2 rounded-full">
                      {getAddressIcon(address.addressType)}
                    </div>
                    <div>
                      <span className="font-medium text-gray-800">
                        {address.addressType}
                      </span>
                      {address.isDefault && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(address)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      aria-label="Edit address"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(address._id)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      aria-label="Delete address"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-gray-800 capitalize">
                  {address.fullName}
                </h3>
                <p className="text-gray-600 mt-1">{address.phone}</p>
                <div className="mt-2 text-gray-700">
                  <p>{address.addressLine1}</p>
                  {address.addressLine2 && <p>{address.addressLine2}</p>}
                  <p>
                    {address.landmark && `${address.landmark}, `}
                    {address.city}, {address.state} {address.postalCode}
                  </p>
                  <p>{address.country}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddAddressModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          fetchAddresses();
        }}
      />
      <EditAddressModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        address={selectedAddress}
        onUpdate={fetchAddresses}
      />
    </div>
  );
};

export default Address;
